import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    linkedSignal,
    signal,
    untracked,
    viewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { SeUserSearchService, SeUserTileDocument } from "./se.user.search.service";
import { SeUserSearchCustomAttributeFiltersComponent } from "./se.user.search.custom.attribute.filters.component";
import {
    areSeUserSearchCustomAttributeFiltersEqual,
    formatSeUserSearchCustomAttributeFilterLabel,
    type SeUserSearchCustomAttributeFilter,
} from "./se.user.search.custom.attribute.util";
import { SeContactBulkEditSeUserService } from "./se.contact.bulk.edit.se.user.service";
import { SeSecurityStore } from "../se.security.store";
import { RoleRights } from "../securityConfig/role.rights";
import { AccessLevels, isMinFls } from "../../../pojo/Fls";
import { AlertService } from "../../../services/alert.service";
import { Table } from "../../s25-table/Table";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { SeUserSearchOrganizationsCellComponent } from "./se.user.search.organizations.cell.component";
import { SeUserSearchRowDetailComponent } from "./se.user.search.row.detail.component";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { DropDownItem } from "../../../pojo/DropDownItem";
import {
    parseStudentEngagementUserSearchStatusFilter,
    StudentEngagementUserLoginStatusQueryValue,
    type StudentEngagementUserSearchStatusFilterModel,
} from "../../../pojo/StudentEngagementUserLoginStatus";
import { S25SidebarComponent } from "../../s25-sidebar/s25.sidebar.component";
import { SidebarStore } from "../../s25-sidebar/s25.sidebar.store";
import { S25EmailComponent } from "../../s25-email/s25.email.component";
import { ContactRole } from "../../../services/organization.service";
import { ContactService } from "../../../services/contact.service";
import { Item } from "../../../pojo/Item";
import { S25Util } from "../../../util/s25-util";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import {
    connect25TemplateOrgIdsParamKey,
    normalizeConnect25TemplateOrgIds,
} from "../email/connect25.email.template.compose.util";
import { S25ModalModule } from "../../s25-modal/s25.modal.module";
import { S25ModalComponent } from "../../s25-modal/s25.modal.component";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";

export interface UserSearchFilter {
    searchTerm: string;
    organizations?: DropDownItem[];
    roles?: DropDownItem[];
    customAttributeFilters?: SeUserSearchCustomAttributeFilter[];
    userStatus: StudentEngagementUserSearchStatusFilterModel;
}

export const DEFAULT_USER_SEARCH_FILTER: UserSearchFilter = {
    searchTerm: "",
    organizations: undefined,
    roles: undefined,
    customAttributeFilters: undefined,
    userStatus: "",
};

export function contactRoleFromUserRow(user: SeUserTileDocument): ContactRole {
    return {
        itemId: user.itemId,
        itemName: user.itemName ?? "",
        itemEmail: user.email,
        isPrimary: false,
        itemTypeId: Item.Ids.Contact,
        role: { itemId: 0, itemName: "User" },
    };
}

@TypeManagerDecorator("s25-ng-se-user-search")
@Component({
    selector: "s25-ng-se-user-search",
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        S25TableModule,
        S25DropdownPaginatedModule,
        S25IconComponent,
        SeUserSearchCustomAttributeFiltersComponent,
        S25SidebarComponent,
        S25EmailComponent,
        S25LoadingInlineModule,
        S25ModalModule,
        S25ButtonComponent,
    ],
    providers: [SidebarStore],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="user-search-container">
            <header class="search-header">
                <div class="header-left">
                    <h1 class="page-title">User Search</h1>
                    @if (totalResults() !== null) {
                        <span class="item-count">{{ totalResults() }} total</span>
                    }
                </div>
            </header>

            <form class="filters-form" (ngSubmit)="runSearch()">
                <div class="filters-container">
                    <div class="search-wrapper" role="search">
                        <label for="se-user-search-q" class="visually-hidden">Search users by name or email</label>
                        <s25-ng-icon [type]="'search'" [size]="18" class="search-icon" [ariaLabel]="false" />
                        <input
                            id="se-user-search-q"
                            class="search-input"
                            type="search"
                            name="seUserSearchQuery"
                            [ngModel]="searchTermDraft()"
                            (ngModelChange)="onSearchTermDraftInput($event)"
                            placeholder="Name or email"
                            autocomplete="off"
                        />
                        @if (searchTermDraft()) {
                            <button
                                class="clear-btn"
                                type="button"
                                aria-label="Clear search"
                                (click)="clearSearchTermDraft()"
                            >
                                <s25-ng-icon [type]="'close'" [size]="14" [ariaLabel]="false" />
                            </button>
                        }
                    </div>

                    <fieldset class="filter-controls">
                        <legend class="visually-hidden">Filter options</legend>
                        <s25-ng-dropdown-multi-search-criteria
                            id="se-user-search-org-filter"
                            class="se-filter-criteria-dropdown"
                            [type]="'studentEngagementOrganizations'"
                            [(value)]="organizationsModel"
                            [placeholder]="'All Organizations'"
                            [hasDone]="false"
                            [ariaLabel]="'Filter by organization'"
                            (done)="onOrganizationsFilterDone($event)"
                        />
                        <s25-ng-dropdown-multi-search-criteria
                            id="se-user-search-role-filter"
                            class="se-filter-criteria-dropdown"
                            [type]="'organizationRoles'"
                            [(value)]="rolesModel"
                            [placeholder]="'All Roles'"
                            [hasDone]="false"
                            [ariaLabel]="'Filter by role'"
                            (done)="onRolesFilterDone($event)"
                        />
                        <select
                            id="se-user-search-status"
                            class="filter-select"
                            name="seUserSearchStatus"
                            aria-label="Filter by status"
                            [ngModel]="userSearchFilter().userStatus"
                            (ngModelChange)="onUserStatusChange($event)"
                        >
                            <option value="">All statuses</option>
                            <option [value]="UserStatusQueryValue.Active">Active</option>
                            <option [value]="UserStatusQueryValue.NoLogin">No login</option>
                            <option [value]="UserStatusQueryValue.LoginDisabled">Login disabled</option>
                        </select>
                        <button class="aw-button aw-button--primary search-btn" type="submit">Search</button>
                        @if (hasActiveFilters()) {
                            <button class="text-btn" type="button" (click)="clearFilters()">Clear filters</button>
                        }
                    </fieldset>

                    <s25-ng-se-user-search-custom-attribute-filters
                        class="ca-filters-panel"
                        [seedFilters]="userSearchFilter().customAttributeFilters ?? []"
                        [resetKey]="customAttributeFiltersResetKey()"
                        (filtersChange)="onCustomAttributeFiltersChange($event)"
                    />
                </div>

                @if (hasActiveFilters()) {
                    <div class="active-filters" role="group" aria-label="Active filters">
                        <span class="filter-label">Active filters:</span>
                        @if (userSearchFilter().searchTerm) {
                            <span class="filter-pill">
                                "{{ userSearchFilter().searchTerm }}"
                                <button
                                    type="button"
                                    (click)="clearAppliedSearchTerm()"
                                    [attr.aria-label]="'Remove search filter: ' + userSearchFilter().searchTerm"
                                >
                                    ×
                                </button>
                            </span>
                        }
                        @for (organization of userSearchFilter().organizations; track organization.itemId) {
                            <span class="filter-pill">
                                {{ organization.itemName }}
                                <button
                                    type="button"
                                    (click)="removeOrganization(organization)"
                                    [attr.aria-label]="'Remove organization filter: ' + organization.itemName"
                                >
                                    ×
                                </button>
                            </span>
                        }
                        @for (role of userSearchFilter().roles; track role.itemId) {
                            <span class="filter-pill">
                                {{ role.itemName }}
                                <button
                                    type="button"
                                    (click)="removeRole(role)"
                                    [attr.aria-label]="'Remove role filter: ' + role.itemName"
                                >
                                    ×
                                </button>
                            </span>
                        }
                        @if (userStatusLabel()) {
                            <span class="filter-pill">
                                {{ userStatusLabel() }}
                                <button
                                    type="button"
                                    (click)="onUserStatusChange('')"
                                    [attr.aria-label]="'Remove status filter: ' + userStatusLabel()"
                                >
                                    ×
                                </button>
                            </span>
                        }
                        @for (
                            customAttributeFilter of userSearchFilter().customAttributeFilters;
                            track customAttributeFilter.filterKey
                        ) {
                            <span class="filter-pill">
                                {{ formatCustomAttributeFilterLabel(customAttributeFilter) }}
                                <button
                                    type="button"
                                    (click)="removeCustomAttributeFilter(customAttributeFilter)"
                                    [attr.aria-label]="
                                        'Remove custom attribute filter: ' +
                                        formatCustomAttributeFilterLabel(customAttributeFilter)
                                    "
                                >
                                    ×
                                </button>
                            </span>
                        }
                    </div>
                }
            </form>

            @if (!hasSearched()) {
                <div class="message-container">
                    <p class="message-title">Add filters or choose Search to list all users</p>
                </div>
            } @else {
                <s25-ng-table
                    #userTable
                    [caption]="'User search results'"
                    [dataSource]="userTableConfig"
                    [hasExpandableRows]="true"
                    [hasSelect]="canBulkSelect()"
                    [hasSelectAll]="canBulkSelect()"
                    [hasTotalRowCount]="true"
                    [noResultsMessage]="'No users match your search'"
                    [unlimitedWidth]="true"
                    (selectedChange)="onUserTableSelectionChange($event)"
                >
                    @if (totalResults() !== null && totalResults()! > 0 && canUseBulkActionsPanel()) {
                        <ng-template #tableTotalCountRight>
                            <div class="bulk-actions-bar">
                                <label class="bulk-actions-label" for="se-user-bulk-actions-select">
                                    {{ bulkActionsButtonLabel() }}
                                </label>
                                <select
                                    id="se-user-bulk-actions-select"
                                    class="filter-select bulk-actions-select"
                                    [ngModel]="bulkActionSelectValue()"
                                    (ngModelChange)="onBulkActionSelected($event)"
                                    aria-label="Bulk actions"
                                >
                                    <option value="" disabled hidden>Choose action…</option>
                                    <option value="clear" [disabled]="!hasBulkSelection()">Clear selection</option>
                                    @if (canBulkEdit()) {
                                        <option value="comment" [disabled]="!hasBulkSelection() || bulkSaving()">
                                            Add comment
                                        </option>
                                    }
                                    @if (canBulkEmail()) {
                                        <option
                                            value="email"
                                            [disabled]="!hasBulkSelection() || isLoadingEmailRecipients()"
                                        >
                                            Email selected
                                        </option>
                                    }
                                </select>
                            </div>
                        </ng-template>
                    }
                </s25-ng-table>
            }
        </div>

        @if (canBulkEmail()) {
            <s25-ng-sidebar [title]="emailSidebarTitle()" [mode]="'overlay'" [width]="600">
                @if (isLoadingEmailRecipients()) {
                    <s25-ng-loading-inline-static />
                } @else if (sidebarStore.isOpen()) {
                    @if (emailSidebarRecipientSummary()) {
                        <p class="sidebar-recipients">{{ emailSidebarRecipientSummary() }}</p>
                    }
                    <s25-ng-email
                        [contextId]="emailContextContactId()"
                        [itemTypeId]="ContactTypeId"
                        [orgMembers]="bulkEmailMembers()"
                        [initialToRecipients]="bulkEmailMembers()"
                        [memberLabel]="'Selected users'"
                        [showFormAssignment]="true"
                        [connect25TemplateOrgIds]="connect25TemplateOrgIdsForSidebar()"
                        (closed)="closeEmailSidebar()"
                    />
                }
            </s25-ng-sidebar>
        }

        @if (canBulkEdit()) {
            <s25-ng-modal #bulkCommentModal title="Add comment to selected users" [type]="'custom'" [size]="'xs'">
                <ng-template #s25ModalBody>
                    <label class="bulk-comment-label" for="se-user-bulk-comment">Comment</label>
                    <textarea
                        id="se-user-bulk-comment"
                        class="aw-input bulk-comment"
                        rows="4"
                        [(ngModel)]="bulkCommentModel"
                    ></textarea>
                </ng-template>
                <ng-template #s25ModalFooter>
                    <div class="bulk-comment-modal-footer">
                        <s25-ng-button
                            [type]="'primary'"
                            [disabled]="!bulkCommentModel.trim() || bulkSaving()"
                            [isLoading]="bulkSaving()"
                            loadingText="Saving…"
                            (clicked)="confirmBulkComment()"
                        >
                            Apply comment
                        </s25-ng-button>
                        <s25-ng-button [type]="'outline'" (clicked)="closeBulkCommentModal()">Cancel</s25-ng-button>
                    </div>
                </ng-template>
            </s25-ng-modal>
        }
    `,
    styles: `
        .user-search-container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 0 1rem 2rem;
        }
        .search-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid #e5e7eb;
        }
        .page-title {
            margin: 0;
            font-size: 1.5rem;
        }
        .item-count {
            margin-left: 0.75rem;
            color: #6b7280;
        }
        .filters-form {
            margin-bottom: 1.5rem;
        }

        .filters-container {
            display: flex;
            gap: 1rem;
            flex-wrap: wrap;
            align-items: flex-start;
        }

        .ca-filters-panel {
            flex: 1 1 100%;
        }

        .visually-hidden {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }

        .search-wrapper {
            flex: 1;
            min-width: 240px;
            position: relative;
        }

        .search-icon {
            position: absolute;
            left: 0.75rem;
            top: 50%;
            transform: translateY(-50%);
            color: var(--color--text-muted, #6b7280);
            pointer-events: none;
        }

        .search-input {
            width: 100%;
            padding: 0.5rem 2rem 0.5rem 2.5rem;
            border: 1px solid var(--color--border-bold, #b3b3b3);
            border-radius: 6px;
            font-size: 0.875rem;
            transition: border-color var(--transition-medium, 200ms) ease;
            background: var(--color--form-bg, white);
            color: var(--color--text, #333);
        }

        .search-input:focus {
            outline: none;
            border-color: var(--color--border-bold-hover, #666);
            box-shadow: var(--shadow--form-focus, 0 0 0 2px #fafaf9, 0 0 0 4px #2573a7);
        }

        .search-input::-webkit-search-cancel-button {
            -webkit-appearance: none;
            appearance: none;
        }

        .clear-btn {
            position: absolute;
            right: 0.5rem;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: var(--color--text-muted, #666);
            cursor: pointer;
            padding: 0.25rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: var(--border-radius, 3px);
        }

        .filter-controls {
            display: flex;
            gap: 0.75rem;
            align-items: flex-start;
            border: none;
            padding: 0;
            margin: 0;
        }

        .filter-select {
            padding: 0.5rem 2rem 0.5rem 0.75rem;
            border: 1px solid var(--color--border-bold, #b3b3b3);
            border-radius: 6px;
            font-size: 0.875rem;
            cursor: pointer;
            appearance: none;
            background: var(--color--form-bg, white)
                url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")
                no-repeat right 0.5rem center;
            background-size: 16px;
            min-width: 140px;
            color: var(--color--text, #333);
        }

        .filter-select:focus {
            outline: none;
            border-color: var(--color--border-bold-hover, #666);
            box-shadow: var(--shadow--form-focus, 0 0 0 2px #fafaf9, 0 0 0 4px #2573a7);
        }

        .text-btn {
            background: none;
            border: none;
            color: var(--color--text-muted, #666);
            font-size: 0.875rem;
            cursor: pointer;
            padding: 0.5rem 0.75rem;
            border-radius: 6px;
            white-space: nowrap;
        }

        .text-btn:hover {
            color: var(--color--text, #333);
            background: var(--color--surface-2-bg, #f0f0f0);
        }

        .search-btn {
            white-space: nowrap;
            align-self: flex-start;
        }

        .active-filters {
            display: flex;
            gap: 0.5rem;
            align-items: center;
            margin-top: 1rem;
            font-size: 0.875rem;
            flex-wrap: wrap;
        }

        .active-filters .filter-label {
            color: var(--color--text-muted, #666);
        }

        .filter-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            padding: 0.25rem 0.625rem;
            background: var(--color--surface-2-bg, #f0f0f0);
            border-radius: var(--border-radius, 3px);
            font-size: 0.8125rem;
            color: var(--color--text, #333);
        }

        .filter-pill button {
            background: none;
            border: none;
            color: var(--color--text-muted, #666);
            cursor: pointer;
            padding: 0.125rem;
            font-size: 1.125rem;
            line-height: 1;
        }

        :host ::ng-deep .filter-controls .se-filter-criteria-dropdown .ngDropdownPaginated {
            padding: 2px;
            border: 1px solid var(--color--border-bold, #b3b3b3) !important;
            border-radius: 6px;
            min-width: 140px;
        }

        :host ::ng-deep .filter-controls .se-filter-criteria-dropdown .ngDropdownPaginated .select2-container {
            font-size: 0.875rem;
        }

        :host
            ::ng-deep
            .filter-controls
            .se-filter-criteria-dropdown
            .ngDropdownPaginated
            .select2-container
            .select2-choice.ui-select-match {
            padding-inline: 5px 2em;
            display: flex;
        }

        :host
            ::ng-deep
            .filter-controls
            .se-filter-criteria-dropdown
            .ngDropdownPaginated
            .select2-container
            .select2-choice.ui-select-match::after {
            right: 4px;
            background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
            width: 17px;
        }

        @media (max-width: 768px) {
            .filters-container {
                flex-direction: column;
            }

            .filter-controls {
                flex-direction: column;
                align-items: stretch;
                width: 100%;
            }

            .filter-select {
                width: 100%;
            }
        }
        .message-container {
            padding: 2rem;
            text-align: center;
            color: #4b5563;
        }
        .bulk-actions-bar {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding-inline-end: 0.5rem;
        }
        .bulk-actions-label {
            font-weight: 600;
            margin: 0;
            font-size: 0.875rem;
            white-space: nowrap;
        }
        .bulk-actions-select {
            min-width: 11rem;
        }
        .bulk-comment-label {
            display: block;
            font-weight: 600;
            margin-bottom: 0.35rem;
        }
        .bulk-comment {
            width: 100%;
            box-sizing: border-box;
        }
        .bulk-comment-modal-footer {
            display: flex;
            gap: 0.5rem;
            flex-wrap: wrap;
        }
        :host ::ng-deep .expanded-content-wrapper {
            max-height: min(70vh, 720px);
            overflow: auto;
        }
        .sidebar-recipients {
            margin: 0 0 1rem;
            font-size: 0.875rem;
            color: var(--color--text-muted, #6b7280);
            line-height: 1.5;
        }
        :host ::ng-deep .email-component {
            padding: 0 1rem 1rem;
        }
    `,
})
export class SeUserSearchComponent {
    /** Exposed for template option {@code [value]} bindings. */
    readonly UserStatusQueryValue = StudentEngagementUserLoginStatusQueryValue;

    private readonly securityStore = inject(SeSecurityStore);
    private readonly destroyRef = inject(DestroyRef);
    protected readonly sidebarStore = inject(SidebarStore);

    private connect25TemplateOrgIdsRequestId = 0;
    private contactIdToUserRow = new Map<number, SeUserTileDocument>();

    protected readonly ContactTypeId = Item.Ids.Contact;

    readonly userTable = viewChild<S25TableComponent>("userTable");

    /**
     * {@link SeUserSearchService} passes {@code paginate_key} through to {@code Reader.getPaginatedList}, which
     * keeps the <em>full</em> matching list in page cache and serves any {@code page_num} as a slice — the same key
     * is valid for random page jumps until the cache expires (see {@code TileSearchService#searchStudentEngagementUsers}).
     */
    readonly userTableConfig: Table.Paginated = {
        type: "paginated",
        columns: [
            { id: "name", header: "Name", export: true },
            { id: "email", header: "Email", export: true },
            { id: "status", header: "Status", export: true },
            { id: "organizations", header: "Organizations", export: true },
        ],
        defaultPageSize: 25,
        pageSizes: [25, 50, 100],
        keepSelectionsOnPageChange: true,
        dataSource: (query) => this.loadUserTablePage(query),
    };

    readonly userSearchFilter = signal<UserSearchFilter>({ ...DEFAULT_USER_SEARCH_FILTER });
    /** Text in the search box; applied to the filter on Enter or Search only. */
    readonly searchTermDraft = linkedSignal(() => this.userSearchFilter().searchTerm);
    protected readonly organizationsModel = linkedSignal(() => this.userSearchFilter().organizations ?? []);
    protected readonly rolesModel = linkedSignal(() => this.userSearchFilter().roles ?? []);
    readonly customAttributeFiltersResetKey = signal(0);
    readonly hasSearched = signal(false);

    protected readonly formatCustomAttributeFilterLabel = formatSeUserSearchCustomAttributeFilterLabel;

    readonly hasActiveFilters = computed(() => SeUserSearchComponent.hasSearchCriteria(this.userSearchFilter()));

    readonly userStatusLabel = computed(() => {
        const status = parseStudentEngagementUserSearchStatusFilter(this.userSearchFilter().userStatus);
        if (!status) return "";
        const labels: Record<StudentEngagementUserLoginStatusQueryValue, string> = {
            [StudentEngagementUserLoginStatusQueryValue.Active]: "Active",
            [StudentEngagementUserLoginStatusQueryValue.NoLogin]: "No login",
            [StudentEngagementUserLoginStatusQueryValue.LoginDisabled]: "Login disabled",
        };
        return labels[status] ?? "";
    });
    bulkCommentModel = "";

    readonly bulkSaving = signal(false);

    readonly selectedContactIds = signal(new Set<number>());

    readonly bulkActionSelectValue = signal("");

    readonly bulkActionsButtonLabel = computed(() => {
        const selectedCount = this.selectedContactIds().size;
        if (!selectedCount) {
            return "Bulk actions";
        }
        return `Bulk actions (${selectedCount} selected)`;
    });

    readonly hasBulkSelection = computed(() => this.selectedContactIds().size > 0);

    private readonly bulkCommentModal = viewChild<S25ModalComponent>("bulkCommentModal");

    /** Total hits from the last completed table load for the current search. */
    readonly totalResults = signal<number | null>(null);

    readonly canBulkEdit = computed(() => {
        if (!this.securityStore.initialized()) {
            return false;
        }
        if (this.securityStore.hasFLSOverride()) {
            return true;
        }
        const accessLevel = this.securityStore.maxRights()[RoleRights.RoleRightsList.SE_USER_BULK_EDIT];
        return isMinFls(accessLevel, AccessLevels.Full);
    });

    readonly canBulkEmail = computed(() => {
        if (!this.securityStore.initialized()) {
            return false;
        }
        return this.securityStore.hasFLSOverride();
    });

    readonly canUseBulkActionsPanel = computed(() => this.canBulkEdit() || this.canBulkEmail());

    readonly canBulkSelect = computed(() => this.canBulkEdit() || this.canBulkEmail());

    readonly bulkEmailMembers = signal<ContactRole[]>([]);
    readonly isLoadingEmailRecipients = signal(false);
    readonly emailSidebarOrgIds = signal<number[]>([]);
    readonly connect25TemplateOrgIdsForSidebar = signal<number[]>([]);
    readonly emailSidebarRecipientSummary = signal("");

    readonly emailSidebarTitle = computed(() => "Email users");

    readonly emailContextContactId = computed(() => this.bulkEmailMembers()[0]?.itemId ?? 0);

    constructor() {
        void this.securityStore.ensureInitialized();

        effect(() => {
            this.userSearchFilter();
            untracked(() => {
                if (!this.hasSearched()) {
                    if (SeUserSearchComponent.hasSearchCriteria(this.userSearchFilter())) {
                        this.hasSearched.set(true);
                    }
                    return;
                }
                this.bulkCommentModel = "";
                this.contactIdToUserRow.clear();
                this.closeEmailSidebar();
                this.clearSelection();
                this.refreshUserTableForNewSearch();
            });
        });

        effect(() => {
            const orgIds = normalizeConnect25TemplateOrgIds(this.emailSidebarOrgIds());
            if (!orgIds.length) {
                untracked(() => this.connect25TemplateOrgIdsForSidebar.set([]));
                return;
            }
            const requestKey = connect25TemplateOrgIdsParamKey(orgIds);
            const requestId = ++this.connect25TemplateOrgIdsRequestId;
            void this.securityStore
                .hasAccessForAny([RoleRights.RoleRightsList.SE_ORG_EMAIL], orgIds, AccessLevels.Read)
                .then((accessByOrgId) => {
                    if (requestId !== this.connect25TemplateOrgIdsRequestId) {
                        return;
                    }
                    const currentKey = connect25TemplateOrgIdsParamKey(this.emailSidebarOrgIds());
                    if (currentKey !== requestKey) {
                        return;
                    }
                    this.connect25TemplateOrgIdsForSidebar.set(orgIds.filter((orgId) => accessByOrgId[orgId]));
                })
                .catch(() => {
                    if (requestId !== this.connect25TemplateOrgIdsRequestId) {
                        return;
                    }
                    const currentKey = connect25TemplateOrgIdsParamKey(this.emailSidebarOrgIds());
                    if (currentKey !== requestKey) {
                        return;
                    }
                    this.connect25TemplateOrgIdsForSidebar.set([]);
                });
        });

        this.destroyRef.onDestroy(() => this.closeEmailSidebar());
    }

    private static hasSearchCriteria(filter: UserSearchFilter): boolean {
        return (
            !!filter.searchTerm.trim() ||
            !!filter.organizations?.length ||
            !!filter.roles?.length ||
            !!filter.customAttributeFilters?.length ||
            !!parseStudentEngagementUserSearchStatusFilter(filter.userStatus)
        );
    }

    protected runSearch(): void {
        this.commitSearchTerm();
        const hadSearched = this.hasSearched();
        this.hasSearched.set(true);
        this.bulkCommentModel = "";
        this.contactIdToUserRow.clear();
        this.closeEmailSidebar();
        this.clearSelection();
        if (hadSearched) {
            this.refreshUserTableForNewSearch();
        }
    }

    protected onSearchTermDraftInput(value: string): void {
        this.searchTermDraft.set(value);
    }

    protected clearSearchTermDraft(): void {
        this.searchTermDraft.set("");
    }

    protected clearAppliedSearchTerm(): void {
        this.userSearchFilter.update((filter) => ({ ...filter, searchTerm: "" }));
        this.searchTermDraft.set("");
    }

    private commitSearchTerm(): void {
        const searchTerm = this.searchTermDraft();
        if (this.userSearchFilter().searchTerm === searchTerm) {
            return;
        }
        this.userSearchFilter.update((filter) => ({ ...filter, searchTerm }));
    }

    protected onUserStatusChange(value: StudentEngagementUserSearchStatusFilterModel): void {
        this.userSearchFilter.update((filter) => ({ ...filter, userStatus: value }));
    }

    onOrganizationsFilterDone(selectedOrganizations: DropDownItem[] | null | undefined): void {
        const organizations = selectedOrganizations?.length ? selectedOrganizations : undefined;
        this.userSearchFilter.update((filter) => ({ ...filter, organizations }));
        this.organizationsModel.set(selectedOrganizations ?? []);
    }

    onRolesFilterDone(selectedRoles: DropDownItem[] | null | undefined): void {
        const roles = selectedRoles?.length ? selectedRoles : undefined;
        this.userSearchFilter.update((filter) => ({ ...filter, roles }));
        this.rolesModel.set(selectedRoles ?? []);
    }

    onCustomAttributeFiltersChange(filters: SeUserSearchCustomAttributeFilter[]): void {
        const customAttributeFilters = filters.length ? filters : undefined;
        const currentApplied = this.userSearchFilter().customAttributeFilters;
        if (areSeUserSearchCustomAttributeFiltersEqual(currentApplied, customAttributeFilters)) {
            return;
        }
        this.userSearchFilter.update((current) => ({ ...current, customAttributeFilters }));
    }

    removeCustomAttributeFilter(customAttributeFilter: SeUserSearchCustomAttributeFilter): void {
        const current = this.userSearchFilter().customAttributeFilters ?? [];
        const updated = current.filter((filter) => filter.filterKey !== customAttributeFilter.filterKey);
        const customAttributeFilters = updated.length ? updated : undefined;
        this.userSearchFilter.update((filter) => ({ ...filter, customAttributeFilters }));
    }

    removeOrganization(organization: DropDownItem): void {
        const current = this.userSearchFilter().organizations ?? [];
        const updated = current.filter((item) => item.itemId !== organization.itemId);
        const organizations = updated.length ? updated : undefined;
        this.userSearchFilter.update((filter) => ({ ...filter, organizations }));
        this.organizationsModel.set(updated);
    }

    removeRole(role: DropDownItem): void {
        const current = this.userSearchFilter().roles ?? [];
        const updated = current.filter((item) => item.itemId !== role.itemId);
        const roles = updated.length ? updated : undefined;
        this.userSearchFilter.update((filter) => ({ ...filter, roles }));
        this.rolesModel.set(updated);
    }

    clearFilters(): void {
        this.userSearchFilter.set({ ...DEFAULT_USER_SEARCH_FILTER });
        this.searchTermDraft.set("");
        this.organizationsModel.set([]);
        this.rolesModel.set([]);
        this.customAttributeFiltersResetKey.update((key) => key + 1);
        this.hasSearched.set(false);
        this.totalResults.set(null);
        this.bulkCommentModel = "";
        this.contactIdToUserRow.clear();
        this.closeEmailSidebar();
        this.clearSelection();
    }

    private async loadUserTablePage(query: Table.PaginatedQuery): Promise<Table.DataSourceResponse | undefined> {
        await this.securityStore.ensureInitialized();
        if (!this.hasSearched()) {
            return { rows: [], totalRows: 0 };
        }

        const apiPage = query.page + 1;
        const paginateKeyForRequest = this.paginateKeyFromTableQuery(query);

        const filter = this.userSearchFilter();
        const organizationIds =
            filter.organizations
                ?.map((organization) => Number(organization.itemId))
                .filter((organizationId) => !Number.isNaN(organizationId) && organizationId > 0) ?? [];
        const roleIds =
            filter.roles?.map((role) => Number(role.itemId)).filter((roleId) => !Number.isNaN(roleId) && roleId > 0) ??
            [];

        const result = await SeUserSearchService.searchUsers(
            filter.searchTerm.trim(),
            {
                page: apiPage,
                page_size: query.pageSize,
                paginate: "T",
            },
            paginateKeyForRequest,
            {
                organizationIds,
                roleIds,
                customAttributeFilters: filter.customAttributeFilters,
                userStatus: parseStudentEngagementUserSearchStatusFilter(filter.userStatus),
            },
        );

        if (result.err_msg) {
            throw new Error(result.err_msg);
        }

        const paginateKey = result.paginate_key;
        const userRows = result.users ?? [];
        for (const userRow of userRows) {
            this.contactIdToUserRow.set(userRow.itemId, userRow);
        }
        this.totalResults.set(result.total_results ?? 0);

        return {
            rows: this.buildUserTableRows(userRows),
            totalRows: result.total_results ?? 0,
            cacheId: paginateKey,
        };
    }

    /** {@code s25-ng-table} passes the last response {@code cacheId}; the service uses it as server {@code paginate_key}. */
    private paginateKeyFromTableQuery(query: Table.PaginatedQuery): string | undefined {
        const rawCacheId = query.cacheId;
        if (rawCacheId === undefined || rawCacheId === null) {
            return undefined;
        }
        if (typeof rawCacheId === "string") {
            return rawCacheId.trim() || undefined;
        }
        return String(rawCacheId);
    }

    private buildUserTableRows(userRows: SeUserTileDocument[]): Table.Row[] {
        const allowBulkSelect = this.canBulkSelect();
        return userRows.map((userRow) => ({
            id: userRow.itemId,
            name: userRow.itemName,
            expandable: true,
            selectable: allowBulkSelect,
            cells: {
                name: { text: userRow.itemName ?? "—" },
                email: { text: userRow.email ?? "—" },
                status: { text: userRow.userStatus ?? "—" },
                organizations: {
                    component: SeUserSearchOrganizationsCellComponent,
                    inputs: {
                        organizations: userRow.organizations ?? [],
                    },
                    textValue: this.formatOrganizations(userRow),
                },
            },
            expandedContent: {
                component: SeUserSearchRowDetailComponent,
                inputs: { userRow },
            },
        }));
    }

    private refreshUserTableForNewSearch(): void {
        const table = this.userTable();
        if (!table) {
            return;
        }
        table.collapseAllRows();
        table.filterValue = "";
        table.sortedColumnId = "";
        table.sortedColumnOrder = "desc";
        void table.refresh(true, true, false);
    }

    resetUserSearch(): void {
        this.clearFilters();
    }

    reload(): void {
        this.refreshUserTableForNewSearch();
    }

    formatOrganizations(userRow: SeUserTileDocument): string {
        const organizationBlocks = userRow.organizations;
        if (!organizationBlocks?.length) {
            return "—";
        }
        return organizationBlocks
            .map((organizationBlock) => organizationBlock.organizationName?.trim())
            .filter((name): name is string => !!name?.length)
            .join(", ");
    }

    onUserTableSelectionChange(selected: Set<Table.Row["id"]>): void {
        const nextSet = new Set<number>();
        for (const rowId of selected) {
            const numeric = typeof rowId === "number" ? rowId : Number.parseInt(String(rowId), 10);
            if (!Number.isNaN(numeric)) {
                nextSet.add(numeric);
            }
        }
        this.selectedContactIds.set(nextSet);
    }

    clearSelection(): void {
        this.selectedContactIds.set(new Set());
        this.userTable()?.clearSelections();
    }

    onBulkActionSelected(action: string): void {
        this.bulkActionSelectValue.set("");
        if (!action) {
            return;
        }
        if (action === "clear") {
            if (!this.hasBulkSelection()) {
                return;
            }
            this.clearSelection();
            return;
        }
        if (action === "email") {
            if (!this.hasBulkSelection() || this.isLoadingEmailRecipients()) {
                return;
            }
            void this.openEmailSidebarForSelection();
            return;
        }
        if (action === "comment") {
            if (!this.hasBulkSelection() || this.bulkSaving()) {
                return;
            }
            void this.openBulkCommentModal();
        }
    }

    async openBulkCommentModal(): Promise<void> {
        this.bulkCommentModel = "";
        await this.bulkCommentModal()?.open();
    }

    closeBulkCommentModal(): void {
        void this.bulkCommentModal()?.close();
    }

    confirmBulkComment(): void {
        void this.applyBulkComment();
    }

    async applyBulkComment(): Promise<void> {
        const contactIds = [...this.selectedContactIds()];
        const commentText = this.bulkCommentModel.trim();
        if (!contactIds.length || !commentText) {
            return;
        }
        const confirmed = await AlertService.confirm(`Add this comment to ${contactIds.length} selected users?`);
        if (!confirmed) {
            return;
        }
        this.bulkSaving.set(true);
        try {
            await SeContactBulkEditSeUserService.putBulkTextComment(contactIds, commentText);
            this.closeBulkCommentModal();
            await AlertService.alert("Comment applied.");
            this.bulkCommentModel = "";
            this.clearSelection();
            this.reload();
        } catch (caughtError) {
            const message = caughtError instanceof Error ? caughtError.message : String(caughtError);
            await AlertService.alert(`Bulk update failed: ${message}`);
        } finally {
            this.bulkSaving.set(false);
        }
    }

    async openEmailSidebarForSelection(): Promise<void> {
        const contactIds = [...this.selectedContactIds()];
        if (!contactIds.length) {
            return;
        }

        this.isLoadingEmailRecipients.set(true);
        const resolvedRoles = await this.resolveContactRolesForIds(contactIds);
        this.isLoadingEmailRecipients.set(false);

        const withEmail = resolvedRoles.filter((role) => role.itemEmail?.trim());
        const skippedCount = contactIds.length - withEmail.length;
        if (!withEmail.length) {
            await AlertService.alert("None of the selected users have an email address on file.");
            return;
        }
        if (skippedCount > 0) {
            await AlertService.alert(
                `${skippedCount} selected user${skippedCount === 1 ? "" : "s"} skipped — no email on file.`,
            );
        }

        this.bulkEmailMembers.set(withEmail);
        this.emailSidebarRecipientSummary.set(this.buildEmailRecipientSummary(withEmail));
        this.emailSidebarOrgIds.set(this.deriveConnect25TemplateOrgIds(contactIds));

        if (!this.sidebarStore.isOpen()) {
            this.sidebarStore.toggle();
        }
    }

    closeEmailSidebar(): void {
        this.sidebarStore.close();
        this.bulkEmailMembers.set([]);
        this.emailSidebarRecipientSummary.set("");
        this.emailSidebarOrgIds.set([]);
        this.connect25TemplateOrgIdsForSidebar.set([]);
    }

    private buildEmailRecipientSummary(members: ContactRole[]): string {
        const count = members.length;
        if (count <= 3) {
            return `Selected: ${members.map((member) => member.itemName).join(", ")}`;
        }
        return `${count} users selected`;
    }

    private deriveConnect25TemplateOrgIds(selectedContactIds: number[]): number[] {
        const filterOrgs = this.userSearchFilter().organizations;
        if (filterOrgs?.length) {
            return filterOrgs
                .map((organization) => S25Util.parseInt(organization.itemId))
                .filter((organizationId) => organizationId != null);
        }
        const orgIdSet = new Set<number>();
        for (const contactId of selectedContactIds) {
            const userRow = this.contactIdToUserRow.get(contactId);
            for (const organizationBlock of userRow?.organizations ?? []) {
                if (organizationBlock.organizationId) {
                    orgIdSet.add(organizationBlock.organizationId);
                }
            }
        }
        return [...orgIdSet];
    }

    private async resolveContactRolesForIds(contactIds: number[]): Promise<ContactRole[]> {
        const roleByContactId = new Map<number, ContactRole>();
        const missingContactIds: number[] = [];

        for (const contactId of contactIds) {
            const userRow = this.contactIdToUserRow.get(contactId);
            if (userRow) {
                roleByContactId.set(contactId, contactRoleFromUserRow(userRow));
            } else {
                missingContactIds.push(contactId);
            }
        }

        if (missingContactIds.length) {
            const fetchedRoles = await this.loadContactRolesFromService(missingContactIds);
            for (const role of fetchedRoles) {
                roleByContactId.set(role.itemId, role);
            }
        }

        return contactIds
            .map((contactId) => roleByContactId.get(contactId))
            .filter((role): role is ContactRole => role != null);
    }

    private async loadContactRolesFromService(contactIds: number[]): Promise<ContactRole[]> {
        const [contactsResponse, contactsError] = await S25Util.Maybe(
            ContactService.getFilterContacts(contactIds, ["address"]),
        );
        if (contactsError) {
            return [];
        }
        const contacts = contactsResponse?.contacts?.contact ?? [];
        const contactList = Array.isArray(contacts) ? contacts : [contacts];
        return contactList.map((contact: Record<string, unknown>) => {
            const contactId = S25Util.parseInt(contact.contact_id as string | number);
            const email =
                S25Util.propertyGetVal(S25Util.propertyGetParentWithChildValue(contact, "address_type", 3), "email") ??
                undefined;
            return {
                itemId: contactId,
                itemName: String(contact.contact_name ?? ""),
                itemEmail: typeof email === "string" ? email : undefined,
                isPrimary: false,
                itemTypeId: Item.Ids.Contact,
                role: { itemId: 0, itemName: "User" },
            };
        });
    }
}
